'use client';

import React from 'react';
import Link from 'next/link';
import { Bell } from 'lucide-react';
import { useI18n } from '@/i18n';

interface NotificationItem {
  id: number;
  type: string;
  title: string;
  body: string;
  href: string | null;
  read_at: string | null;
  created_at: string;
}

export default function NotificationBell() {
  const { t } = useI18n();
  const [open, setOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [notifications, setNotifications] = React.useState<NotificationItem[]>([]);

  const loadUnreadCount = React.useCallback(async () => {
    try {
      const res = await fetch('/api/notifications/unread-count', { method: 'GET' });
      const json = await res.json();
      if (res.ok && json.ok) {
        setUnreadCount(Number(json.unreadCount) || 0);
      }
    } catch {
      // ignore
    }
  }, []);

  const loadNotifications = React.useCallback(async () => {
    try {
      const res = await fetch('/api/notifications/list?limit=10', { method: 'GET' });
      const json = await res.json();
      if (res.ok && json.ok) {
        setNotifications(Array.isArray(json.notifications) ? json.notifications : []);
      }
    } catch {
      // ignore
    }
  }, []);

  React.useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  React.useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open, loadNotifications]);

  const markRead = React.useCallback(async (ids: number[]) => {
    if (ids.length === 0) return;
    try {
      const res = await fetch('/api/notifications/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      const json = await res.json();
      if (res.ok && json.ok) {
        setNotifications((prev) =>
          prev.map((item) => (ids.includes(item.id) ? { ...item, read_at: item.read_at ?? new Date().toISOString() } : item)),
        );
        loadUnreadCount();
      }
    } catch {
      // ignore
    }
  }, [loadUnreadCount]);

  const markAllRead = async () => {
    try {
      const res = await fetch('/api/notifications/mark-all-read', { method: 'POST' });
      const json = await res.json();
      if (res.ok && json.ok) {
        setNotifications((prev) => prev.map((item) => ({ ...item, read_at: item.read_at ?? new Date().toISOString() })));
        setUnreadCount(0);
      }
    } catch {
      // ignore
    }
  };

  const handleItemClick = (item: NotificationItem) => {
    if (!item.read_at) {
      markRead([item.id]);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="relative flex items-center justify-center p-2 rounded hover:bg-white dark:hover:bg-gray-800 transition-colors"
        aria-label={t('notifications.ariaLabel')}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 rounded-full bg-red-500 text-white text-[10px] px-1.5 py-0.5">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 z-50">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('notifications.title')}
            </span>
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-blue-600 hover:underline"
            >
              {t('notifications.markAllRead')}
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-500">{t('notifications.empty')}</p>
            ) : (
              notifications.map((item) => (
                <div key={item.id} className="border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                  {item.href ? (
                    <Link
                      href={item.href}
                      onClick={() => handleItemClick(item)}
                      className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <p className={`text-sm font-medium ${item.read_at ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}>
                        {item.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{item.body}</p>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleItemClick(item)}
                      className="block w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <p className={`text-sm font-medium ${item.read_at ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}>
                        {item.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{item.body}</p>
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
